import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radar, Loader2, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/login", { email, password });
      login(data);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(8,145,178,0.12),transparent_55%)]" />
      <div className="w-full max-w-md relative">
        <div className="card-surface p-8 lg:p-10" data-testid="admin-login-page">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-10 h-10 rounded-md bg-blue-600/20 border border-blue-500/30">
              <ShieldCheck className="w-5 h-5 text-cyan-400" />
            </span>
            <span className="font-display font-bold text-xl text-white">GEOTRAQ <span className="text-cyan-400">AI</span></span>
          </div>
          <h1 className="font-display text-2xl font-bold text-white mt-6">Administrator Login</h1>
          <p className="text-sm text-slate-400 mt-1">Restricted access. All actions are audit logged.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <Label className="text-slate-300 text-xs tracking-widest uppercase">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="admin@geotraq.ai"
                className="mt-1.5 bg-secondary/50 border-white/10 h-11" data-testid="admin-email-input" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs tracking-widest uppercase">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••"
                className="mt-1.5 bg-secondary/50 border-white/10 h-11" data-testid="admin-password-input" />
            </div>
            {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded px-3 py-2" data-testid="admin-login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 font-semibold h-11" data-testid="admin-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "LOGIN TO ADMIN PORTAL"}
            </Button>
          </form>
        </div>
        <Link to="/" className="block text-center text-xs text-slate-500 mt-6 hover:text-cyan-400 transition-colors">
          <Radar className="w-3 h-3 inline mr-1" /> Back to GEOTRAQ AI website
        </Link>
      </div>
    </div>
  );
}
